import React, { useEffect, useState } from "react";
import { Loader } from "@swiggy-private/dls-web-core/dist/components/Loader";
import { ErrorComponent, CartOrdersPageSkeleton } from "@swiggy-private/portal-dineout-ui";
import { useRouter } from "next/compat/router";
import { ERROR_MESSAGES } from "./constants";
import { LoaderContainer } from "./styles";
import { ROUTES, STORAGE_KEYS } from "../../constants";
import { MenuPrefetchContext } from "../../context/MenuPrefetchContext";
import { useFetchDigitalMenuConfig } from "../../hooks/useFetchDigitalMenuConfig";
import { useMenuDataPrefetch } from "../../hooks/useMenuDataPrefetch";
import { useQrScanTracking } from "../../hooks/useQrScanTracking";
import { isBrowserEnv } from "../../utils/browser";

interface IDigitalMenuConfigLoaderProps {
    children: React.ReactNode;
}

const ConfigLoader: React.FC = () => (
    <LoaderContainer>
        <Loader type="primary" size="large" />
    </LoaderContainer>
);

const getTableConfigFromStorage = (): { restaurantId: string; tableId: string } => {
    if (!isBrowserEnv()) return { restaurantId: "", tableId: "" };

    try {
        const storedConfig = sessionStorage.getItem(STORAGE_KEYS.TABLE_CONFIG);
        if (storedConfig) {
            const parsed = JSON.parse(storedConfig);
            return {
                restaurantId: parsed?.restaurantId || "",
                tableId: parsed?.tableId || "",
            };
        }
    } catch {
        // Ignore parsing errors
    }
    return { restaurantId: "", tableId: "" };
};

export const DigitalMenuConfigLoader: React.FC<IDigitalMenuConfigLoaderProps> = ({ children }) => {
    const router = useRouter();
    const [restaurantId, setRestaurantId] = useState("");
    const [tableId, setTableId] = useState("");
    const [isResolved, setIsResolved] = useState(false);

    // Config API - always runs (called once per session)
    const {
        isFetching: isConfigFetching,
        hasFetched: hasConfigFetched,
        hasError: hasConfigError,
        retryFetchConfig,
    } = useFetchDigitalMenuConfig({
        restaurantId,
        isRouterReady: router?.isReady ?? false,
    });

    useEffect(() => {
        if (!router?.isReady) return;

        // Get restaurantId and tableId from query params or sessionStorage
        const queryRestaurantId = router.query.restaurant_id as string;
        const queryTableId = router.query.table_id as string;
        const storage = getTableConfigFromStorage();

        setRestaurantId(queryRestaurantId || storage.restaurantId || "");
        setTableId(queryTableId || storage.tableId || "");
        setIsResolved(true);
    }, [router?.isReady, router?.query]);

    // Prefetch menu and pin data in parallel with config for menu routes
    // This runs even while config is still loading to avoid waterfall.
    // The optimistic prefetch checks consent from the last remembered answer in storage
    // (falling back to true — most users agree — on a first-ever visit, or while the LDA
    // config isn't known yet); the age gate's decline path corrects it via refetchWithConsent()
    // if the guess turns out wrong once config confirms the real, windowed answer.
    const { refetchWithConsent } = useMenuDataPrefetch({
        restaurantId,
        tableId,
    });

    // Fires the qr-scan API once per scan (logged-in + rid/tid in URL, V1 only).
    // Logged-out scans fire after the post-login reload back to the menu.
    useQrScanTracking();

    // Wait for router and params to be resolved
    if (!router?.isReady || !isResolved) {
        return <ConfigLoader />;
    }

    // If no restaurantId, let the app handle it (QrLayout will show error)
    if (!restaurantId) {
        return <>{children}</>;
    }

    // Wait for config to be loaded (UI still blocks on config for theme)
    if (!hasConfigFetched || isConfigFetching) {
        const skeletonPages = [ROUTES.CART_ORDERS, ROUTES.CART, ROUTES.TRACK];
        const showSkeleton = skeletonPages.includes(router.pathname);

        return showSkeleton ? <CartOrdersPageSkeleton /> : <ConfigLoader />;
    }

    // Show error UI if config API failed
    if (hasConfigError) {
        return (
            <ErrorComponent
                heading={ERROR_MESSAGES.heading}
                description={ERROR_MESSAGES.description}
                ctaText={ERROR_MESSAGES.ctaText}
                onCtaClick={retryFetchConfig}
                showFullPage={true}
            />
        );
    }

    return <MenuPrefetchContext.Provider value={{ refetchWithConsent }}>{children}</MenuPrefetchContext.Provider>;
};
